import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { cn } from "@postman/core/cn";
import { API_BASE } from "@/lib/api";
import {
  applicantsQuery,
  coordQuery,
  useDecide,
  type ApplicantRow,
  type Outcome,
  type StateFilter,
} from "@/lib/coord";
import { copyText, download, toCsv } from "@/lib/export";
import { StatePill } from "@/components/pipeline";
import {
  Button,
  Callout,
  Empty,
  Input,
  Label,
  Pill,
  Rows,
} from "@/components/ui";
import { Select } from "@/components/pick";

export const Route = createFileRoute("/_auth/coord/$vertical/")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(applicantsQuery(params.vertical, "all")),
  component: Applicants,
});

/** Outcomes, worded as the sentence the coordinator is actually choosing. */
const OUTCOMES: { value: Outcome; label: string }[] = [
  { value: "advanced", label: "Advance to the next round" },
  { value: "selected", label: "Select — they are in" },
  { value: "rejected", label: "Not this time" },
  { value: "pending", label: "Undo the decision" },
];

const FILTERS: { value: StateFilter; label: string }[] = [
  { value: "all", label: "Everyone" },
  { value: "active", label: "In progress" },
  { value: "selected", label: "Selected" },
  { value: "rejected", label: "Not selected" },
  { value: "in_progress", label: "Started, not sent" },
  { value: "withdrawn", label: "Withdrawn" },
];

/**
 * The applicant list.
 *
 * Filtering by state happens on the server (it is an indexed query), and
 * everything else — search, the round filter, sort — happens here on rows that
 * are already in the browser. That split is what lets export and copy-emails
 * work on EXACTLY what is on screen without a second implementation of the
 * filter, which is the one way an export goes wrong that actually matters: a
 * shortlist email that quietly goes to everyone.
 *
 * No pixel field on this page. A canvas repainting under a list that scrolls
 * for three hundred rows is where the jank comes from — see CLAUDE.md.
 */
function Applicants() {
  const { vertical } = Route.useParams();
  const [state, setState] = useState<StateFilter>("all");
  const [round, setRound] = useState("all");
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const overview = useSuspenseQuery(coordQuery(vertical));
  const { data } = useSuspenseQuery(applicantsQuery(vertical, state));

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return data.items.filter((row) => {
      if (round !== "all" && row.currentRoundId !== round) return false;
      if (!needle) return true;
      return (
        row.user.name.toLowerCase().includes(needle) ||
        row.user.email.toLowerCase().includes(needle) ||
        (row.user.bitsId ?? "").toLowerCase().includes(needle) ||
        (row.branch ?? "").toLowerCase().includes(needle)
      );
    });
  }, [data.items, q, round]);

  const selected = rows.filter((r) => picked.has(r.id));
  const allShown = rows.length > 0 && rows.every((r) => picked.has(r.id));

  function toggle(id: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setPicked(allShown ? new Set() : new Set(rows.map((r) => r.id)));
  }

  function exportCsv() {
    const csv = toCsv(
      [
        "Name",
        "BITS ID",
        "Email",
        "Branch",
        "Phone",
        "Admission year",
        "State",
        "Round",
        "Submitted",
        "Late",
        "Also selected by",
      ],
      rows.map((r) => [
        r.user.name,
        r.user.bitsId,
        r.user.email,
        r.branch,
        r.phone,
        r.user.admissionYear,
        r.state,
        r.currentRound ?? "",
        r.submittedAt ? new Date(r.submittedAt).toISOString() : "",
        r.late ? "late" : "",
        r.alsoSelectedBy,
      ]),
    );
    const stamp = new Date().toISOString().slice(0, 10);
    download(`${vertical}-applicants-${state}-${stamp}.csv`, csv);
  }

  return (
    <div className="flex flex-col gap-8">
      <Counts counts={overview.data.counts} />

      <div className="flex flex-wrap items-end gap-4">
        <div className="flex min-w-[14rem] flex-1 flex-col gap-2">
          <Label htmlFor="q">Search</Label>
          <Input
            id="q"
            value={q}
            placeholder="Name, BITS ID, email, branch"
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="state">State</Label>
          <Select
            id="state"
            value={state}
            options={FILTERS}
            onChange={(next) => {
              setState(next);
              setPicked(new Set());
            }}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="round">Waiting in</Label>
          <Select
            id="round"
            value={round}
            options={[
              { value: "all", label: "Any round" },
              ...data.rounds.map((r) => ({ value: r.id, label: r.name })),
            ]}
            onChange={setRound}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 border-y border-line py-3">
        <p className="mono text-ink-3">
          {rows.length} shown
          {selected.length > 0 ? ` · ${selected.length} selected` : ""}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <CopyEmails rows={selected.length > 0 ? selected : rows} />
          <Button variant="ghost" className="h-9 px-3" onClick={exportCsv}>
            Export CSV
          </Button>
        </div>
      </div>

      {selected.length > 0 ? (
        <Decide
          vertical={vertical}
          rounds={data.rounds}
          ids={selected.map((r) => r.id)}
          onDone={() => setPicked(new Set())}
        />
      ) : null}

      {rows.length === 0 ? (
        <Empty>
          Nobody here yet. Applications appear the moment someone sends the
          form — check that the round exists under Rounds &amp; form.
        </Empty>
      ) : (
        <Rows>
          <div className="flex items-center gap-4 bg-paper-3 px-4 py-3">
            <input
              type="checkbox"
              aria-label="Select everyone shown"
              checked={allShown}
              onChange={toggleAll}
              className="size-4 accent-[color:var(--accent)]"
            />
            <span className="mono text-ink-3">Applicant</span>
          </div>
          {rows.map((row) => (
            <Row
              key={row.id}
              vertical={vertical}
              row={row}
              checked={picked.has(row.id)}
              onToggle={() => toggle(row.id)}
            />
          ))}
        </Rows>
      )}
    </div>
  );
}

function Counts({ counts }: { counts: Record<string, number> }) {
  const cells: [string, number][] = [
    ["Applied", counts.total ?? 0],
    ["In progress", counts.active ?? 0],
    ["Selected", counts.selected ?? 0],
    ["Not selected", counts.rejected ?? 0],
    ["Withdrawn", counts.withdrawn ?? 0],
  ];
  return (
    <dl className="grid grid-cols-2 border-t border-line sm:grid-cols-5">
      {cells.map(([label, n]) => (
        <div key={label} className="flex flex-col gap-1 border-b border-line py-4 pr-4">
          <dt className="mono text-ink-3">{label}</dt>
          <dd className="display text-[28px] tabular-nums">{n}</dd>
        </div>
      ))}
    </dl>
  );
}

function Row({
  vertical,
  row,
  checked,
  onToggle,
}: {
  vertical: string;
  row: ApplicantRow;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <div className={cn("flex items-start gap-4 px-4 py-4", checked && "bg-paper-3")}>
      <input
        type="checkbox"
        aria-label={`Select ${row.user.name}`}
        checked={checked}
        onChange={onToggle}
        className="mt-1 size-4 shrink-0 accent-[color:var(--accent)]"
      />

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <Link
            to="/coord/$vertical/applicants/$id"
            params={{ vertical, id: row.id }}
            className="text-[16px] underline-offset-4 hover:underline"
          >
            {row.user.name}
          </Link>
          <span className="mono text-ink-3">{row.user.bitsId ?? row.user.email}</span>
          {row.late ? <Pill tone="warn">Late</Pill> : null}
          {row.alsoSelectedBy.length > 0 ? (
            /* The badge that stops two verticals both planning around the
               same person without knowing it. */
            <Pill tone="good">Also selected by {row.alsoSelectedBy.join(", ")}</Pill>
          ) : null}
        </div>

        <p className="mono text-ink-3">
          {row.branch ?? "no branch"}
          {row.currentRound
            ? ` · ${row.currentRound} — ${row.submittedCurrent ? "with you" : "with them"}`
            : ""}
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-4">
        {row.resumeId ? (
          <a
            href={`${API_BASE}/files/${row.resumeId}`}
            target="_blank"
            rel="noreferrer"
            className="mono text-ink-3 underline underline-offset-4 hover:text-ink"
          >
            Resume
          </a>
        ) : null}
        <StatePill state={row.state} />
      </div>
    </div>
  );
}

/** Emails for whoever is on screen, ready to paste into a compose window. */
function CopyEmails({ rows }: { rows: ApplicantRow[] }) {
  const [said, setSaid] = useState<string | null>(null);
  return (
    <Button
      variant="ghost"
      className="h-9 px-3"
      onClick={async () => {
        const ok = await copyText(rows.map((r) => r.user.email).join(", "));
        setSaid(ok ? `${rows.length} copied` : "Could not copy");
        setTimeout(() => setSaid(null), 2000);
      }}
    >
      {said ?? `Copy ${rows.length} ${rows.length === 1 ? "email" : "emails"}`}
    </Button>
  );
}

/**
 * Decide a round for everyone ticked.
 *
 * One request for the whole set. The server skips anyone not actually sitting
 * in that round and reports how many it skipped, which is deliberate: a
 * shortlist built by ticking boxes will occasionally include somebody who was
 * already decided, and refusing the entire batch over it would be worse than
 * saying so.
 */
function Decide({
  vertical,
  rounds,
  ids,
  onDone,
}: {
  vertical: string;
  rounds: { id: string; name: string }[];
  ids: string[];
  onDone: () => void;
}) {
  const decide = useDecide(vertical);
  const [roundId, setRoundId] = useState(rounds[0]?.id ?? "");
  const [outcome, setOutcome] = useState<Outcome>("advanced");
  const [note, setNote] = useState("");
  const [result, setResult] = useState<string | null>(null);

  if (rounds.length === 0) return null;

  async function go() {
    setResult(null);
    const out = await decide.mutateAsync({ applicationIds: ids, roundId, outcome, note });
    setResult(
      out.skipped.length > 0
        ? `${out.decided.length} recorded, ${out.skipped.length} skipped — they were not in that round.`
        : `${out.decided.length} recorded.`,
    );
    setNote("");
    onDone();
  }

  return (
    <div className="flex flex-col gap-4 border border-line bg-paper-2 p-5">
      <p className="mono text-ink-3">Decide for {ids.length} selected</p>

      <div className="flex flex-wrap items-end gap-4">
        <div className="flex min-w-[12rem] flex-col gap-2">
          <Label htmlFor="decide-round">Round</Label>
          <Select
            id="decide-round"
            value={roundId}
            options={rounds.map((r) => ({ value: r.id, label: r.name }))}
            onChange={setRoundId}
          />
        </div>
        <div className="flex min-w-[12rem] flex-col gap-2">
          <Label htmlFor="decide-outcome">Outcome</Label>
          <Select
            id="decide-outcome"
            value={outcome}
            options={OUTCOMES}
            onChange={setOutcome}
          />
        </div>
        <div className="flex min-w-[16rem] flex-1 flex-col gap-2">
          <Label htmlFor="decide-note">Note to them (optional)</Label>
          <Input
            id="decide-note"
            value={note}
            maxLength={1000}
            placeholder="Shown to the applicant with the decision"
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <Button onClick={go} disabled={decide.isPending || !roundId}>
          {decide.isPending ? "Recording" : "Record"}
        </Button>
      </div>

      {decide.error ? <Callout tone="error">{(decide.error as Error).message}</Callout> : null}
      {result ? <Callout>{result}</Callout> : null}
    </div>
  );
}
